/**
 * Linear-RGB pigment constants for shaders (art §2.1 tokens through `srgbToLinear`; regenerate from
 * `src/content/palette.ts` if a token changes — tests/engine/contract.test.ts checks them against the palette).
 */
import tgpu from 'typegpu';
import * as d from 'typegpu/data';

export const PIGMENT_LINEAR = {
  inkDeep: [0.0086, 0.0056, 0.004], ink: [0.0242, 0.0123, 0.007], inkFaded: [0.1022, 0.0612, 0.0319],
  leadWhite: [0.9301, 0.8796, 0.7529], vellum: [0.8796, 0.7758, 0.5457], vellumAged: [0.7913, 0.6514, 0.3663],
  vellumShadow: [0.6105, 0.4508, 0.2086], char: [0.0423, 0.0176, 0.007], lapisNight: [0.0048, 0.0116, 0.0742],
  lapis: [0.011, 0.0284, 0.1946], lapisLight: [0.0203, 0.063, 0.3916], azurite: [0.0482, 0.1274, 0.4342],
  lapisPale: [0.159, 0.2384, 0.5776], vermilion: [0.5776, 0.0343, 0.013], rubric: [0.3916, 0.0232, 0.0086],
  minium: [0.7454, 0.1413, 0.0273], bole: [0.2705, 0.0423, 0.016], sinopia: [0.3231, 0.0685, 0.0273],
  goldDeep: [0.3325, 0.1746, 0.0176], gold: [0.6584, 0.3763, 0.0382], goldLight: [0.8963, 0.6795, 0.1946],
  glint: [1.0, 0.9047, 0.5395], shellGold: [0.5395, 0.3231, 0.0595], copperLeaf: [0.4621, 0.1195, 0.0242],
  verdigris: [0.0452, 0.2623, 0.1746], terreVerte: [0.2542, 0.3231, 0.147], ochre: [0.5841, 0.3515, 0.0685],
  ochreLight: [0.6867, 0.4851, 0.2542], umber: [0.107, 0.0423, 0.016], terracotta: [0.3813, 0.1022, 0.0423],
  folium: [0.1946, 0.0437, 0.1329], ironGray: [0.1559, 0.162, 0.1878], intonaco: [0.8308, 0.7231, 0.5647],
  foxing: [0.4851, 0.2542, 0.0762],
} as const;

const c3 = (k: keyof typeof PIGMENT_LINEAR) => {
  const v = PIGMENT_LINEAR[k];
  return tgpu.const(d.vec3f, d.vec3f(v[0], v[1], v[2]));
};

export const P_INK = c3('ink');
export const P_INK_DEEP = c3('inkDeep');
export const P_INK_FADED = c3('inkFaded');
export const P_LEAD_WHITE = c3('leadWhite');
export const P_VELLUM = c3('vellum');
export const P_VELLUM_AGED = c3('vellumAged');
export const P_VELLUM_SHADOW = c3('vellumShadow');
export const P_CHAR = c3('char');
export const P_LAPIS_NIGHT = c3('lapisNight');
export const P_LAPIS = c3('lapis');
export const P_LAPIS_LIGHT = c3('lapisLight');
export const P_LAPIS_PALE = c3('lapisPale');
export const P_AZURITE = c3('azurite');
export const P_VERMILION = c3('vermilion');
export const P_RUBRIC = c3('rubric');
export const P_MINIUM = c3('minium');
export const P_BOLE = c3('bole');
export const P_SINOPIA = c3('sinopia');
export const P_GOLD_DEEP = c3('goldDeep');
export const P_GOLD = c3('gold');
export const P_GOLD_LIGHT = c3('goldLight');
export const P_GLINT = c3('glint');
export const P_SHELL_GOLD = c3('shellGold');
export const P_COPPER = c3('copperLeaf');
export const P_VERDIGRIS = c3('verdigris');
export const P_TERRE_VERTE = c3('terreVerte');
export const P_OCHRE = c3('ochre');
export const P_OCHRE_LIGHT = c3('ochreLight');
export const P_UMBER = c3('umber');
export const P_TERRACOTTA = c3('terracotta');
export const P_FOLIUM = c3('folium');
export const P_IRON = c3('ironGray');
export const P_INTONACO = c3('intonaco');
export const P_FOXING = c3('foxing');
